import {useViewerStore} from "../store/store";
import {useCurrentUser} from "../contexts/UserContext";
import {
    GetMapJson,
    GetRoundNum,
    ifRoofDeactivated
} from "../components/Layout-Components/Editor-Layout/Three-JS-Viewer/Controllers/Common";

export const useGetSaveDesignData = (rsaStatus?: string, annualUsage?: number) => {
    const {user} = useCurrentUser();

    const {
        sceneViewer,
        selectedDesignIndex,
        designList,
        aHJ_Township: ahjTown,
        activeModule,
        siteAddress: address,
        roofInfoArr,
        accessoryBuildingList,
        efficiency,
        designID,
        totalSystemSize,
        deactivatedRoofIdArr,
        modeCount,
        weightedSolarAverage,
        selectedLossesVendor,
        actualWattage,
    } = useViewerStore(state => state);

    const wattage = activeModule?.power ?? 0;

    return async (): Promise<SaveSiteDetailsRequest> => {
        const opportunityId = sessionStorage.getItem('OppurtunityId')!;
        const salesForceID = sessionStorage.getItem('SalesForceId')!;
        const city = sessionStorage.getItem('city');
        const latitude = String(sceneViewer.mapCoordinates.latitude);
        const longitude = String(sceneViewer.mapCoordinates.longitude);
        const stateCode = sessionStorage.getItem('stateCode');
        const postalCode = sessionStorage.getItem('postalCode');
        const directLead = sessionStorage.getItem('directLead')!;
        const efficiencyOverride = useViewerStore.getState().efficiencyOverride;
        const waitForIdealProductionValue = async () => {
            let savedAnnualIdealMultiplier = useViewerStore.getState().savedAnnualIdealMultiplier;
            while (modeCount && savedAnnualIdealMultiplier <= 0) {
                await new Promise(resolve => setTimeout(resolve, 100));
                savedAnnualIdealMultiplier = useViewerStore.getState().savedAnnualIdealMultiplier;
            }
            return savedAnnualIdealMultiplier;
        };

        const idealProductionValue = await waitForIdealProductionValue();

        const mapJson = GetMapJson(sceneViewer);

        const getNodesFromFace = (face: any) => {
            return face.shell.map((index: any) => mapJson?.roofs[0].nodes[index]);
        };

        const nodeDistance = (a: any, b: any) => {
            const dx = a.X - b.X;
            const dy = a.Y - b.Y;
            const dz = a.Z - b.Z;
            return Math.sqrt(dx * dx + dy * dy + dz * dz);
        };

        const nodeAverage = (a: any, b: any) => ({
            X: (a.X + b.X) / 2,
            Y: (a.Y + b.Y) / 2,
            Z: (a.Z + b.Z) / 2,
        });

        const toInt = (x: any) => (x ? parseInt(GetRoundNum(x).toString(), 10) : 0);
        const toDecimal = (x: any) => {
            const val = x ? parseFloat(x) : 0;
            return !isNaN(val) ? val : 0;
        };

        const moduleTypeMappings = [
            { key: "Hanwha - Q.PEAK DUO BLK ML-G10+ 400", value: "QPEAK DUO BLK ML-G10+ 400W" },
            { key: "Hanwha - Q.PEAK DUO BLK ML-G10 plus 405", value: "QPEAK DUO BLK ML-G10+ 405W" },
            { key: "Hanwha - Q.PEAK DUO BLK ML-G10 plus 410", value: "QPEAK DUO BLK ML-G10+ 410W" },
            { key: "Hanwha - Q.PEAK DUO BLK ML-G10 plus 410 / TS -Eng only use", value: "QPEAK DUO BLK ML-G10+ TS" }
        ];

        const buildingDetails = mapJson?.buildings.map((building, buildingIdx) => {
            const roofs = mapJson.roofs[0].faces.filter(face => face.buildingName === building.name);
            let maxNodeHeight = Math.max(...roofs.map(face => getNodesFromFace(face).filter((node: any) => node).map((node: any) => node.Z)).flat());

            if (building.isFlatRoofBuilding) {
                maxNodeHeight = 0;
            }
            const buildingInfo = designList[selectedDesignIndex]?.Buildings ? designList[selectedDesignIndex]?.Buildings[buildingIdx] : null;
            return {
                sf_Account_id: opportunityId,
                highest_Peak_to_Ground__c: maxNodeHeight,
                principal_Structure: !accessoryBuildingList.includes(building.name),
                latitude: latitude,
                longitude: longitude,
                Building_Name: `Building ${building.name}`,
                buildingType: building.buildingType,
                attic_Access_Id: buildingInfo?.Attic_Access_Id ?? null,
                why_No_Attic_Access_Id: buildingInfo?.Why_No_Attic_Access_Id ?? null,
                attic_Access_Notes: buildingInfo?.Attic_Access_Notes ?? "",
                layers_of_Shingles_Id: buildingInfo?.Layers_of_Shingles_Id ?? null,
                all_Roof_The_Same: buildingInfo?.All_Roof_The_Same ?? false,
                number_Of_Stories: buildingInfo?.Number_Of_Stories ?? null,
                roofDetails: roofs.map((face, roofIdx) => {
                    const designation = `R${face.roof_tag_id}`;
                    const roofInfo = roofInfoArr.find((info: any) => info.roofFaceId === face.roofFaceId);
                    const edges = mapJson.roofs[0].edges.filter(edge => edge.nodes.some((node: any) => face.shell.includes(node)));
                    const roofAttr = buildingInfo ? buildingInfo.Roofs[roofIdx] : null;
                    const solarAccess = toDecimal(roofInfo?.solarAccess);
                    const ridges = edges.filter(edge => edge.type === 'ridge');
                    const eaves = edges.filter(edge => edge.type === 'eave');

                    let maxSpan = 0;

                    for (const eave of eaves) {
                        for (const ridge of ridges) {
                            const dist = nodeDistance(
                                nodeAverage(mapJson.roofs[0].nodes[eave.nodes[0]], mapJson.roofs[0].nodes[eave.nodes[1]]),
                                nodeAverage(mapJson.roofs[0].nodes[ridge.nodes[0]], mapJson.roofs[0].nodes[ridge.nodes[1]])
                            );

                            if (dist >= maxSpan) {
                                maxSpan = dist;
                            }
                        }
                    }

                    const activeModuleName = moduleTypeMappings.find(item => item.key === activeModule?.text)?.value ?? activeModule?.text;
                    const activeInd = deactivatedRoofIdArr.find((r: number) => r === face.roof_tag_id) ? false : face.isRoofSquareActive ?? true;

                    return {
                        designation: designation,
                        face: '',
                        modules: face.moduleArr?.length.toString() ?? '',
                        layouts: '0',
                        tilt: face.pitch,
                        azimuth: face.azimuth,
                        sF_Roof_Object_Id: roofAttr?.SF_roof_object_id ?? '',
                        tag_Point: designation,
                        production_Multiplier: roofInfo?.roofMultiplier ?? 0,
                        shade_Points: face.shadeInfo.length.toString(),
                        shade_Weights: face.ptHashAverage?.toString() ?? '',
                        roof_Height: face.height,
                        roof_Stories: face.height / 10,
                        normal_Square: face.normalSquare,
                        low_Square: face.lowSquare,
                        active_Ind: activeInd,
                        pitch_Override: toInt(face.pitchOverride),
                        name: face.roof_tag_id ? `Roof ${face.roof_tag_id}` : "Roof N/A",
                        max_Modules: 0,
                        is_Remote_Site_Assessment: 'Yes',
                        number_of_Modules: face.moduleArr?.length ?? null,
                        weather_Station: mapJson.weatherStation ?? '',
                        current_Array_Size: toDecimal(roofInfo?.arraySize),
                        shade_LastModifiedBy: '', // redundant
                        shade: solarAccess,
                        tsrf: toInt(roofInfo?.efficiency),
                        monthlyWeightArr: face.monthlyWeightArr ?? [],
                        solar_Access_January: face.ptHashMonth?.[0] ?? 0,
                        solar_Access_February: face.ptHashMonth?.[1] ?? 0,
                        solar_Access_March: face.ptHashMonth?.[2] ?? 0,
                        solar_Access_April: face.ptHashMonth?.[3] ?? 0,
                        solar_Access_May: face.ptHashMonth?.[4] ?? 0,
                        solar_Access_June: face.ptHashMonth?.[5] ?? 0,
                        solar_Access_July: face.ptHashMonth?.[6] ?? 0,
                        solar_Access_August: face.ptHashMonth?.[7] ?? 0,
                        solar_Access_September: face.ptHashMonth?.[8] ?? 0,
                        solar_Access_October: face.ptHashMonth?.[9] ?? 0,
                        solar_Access_November: face.ptHashMonth?.[10] ?? 0,
                        solar_Access_December: face.ptHashMonth?.[11] ?? 0,
                        production: {
                            actualAnnual: toInt(roofInfo?.actual_production) ?? 0,
                            monthly: [
                                {
                                    month: "January",
                                    actual: roofInfo?.monthlyProductionEstimateData?.January.actual_production ?? 0
                                },
                                {
                                    month: "February",
                                    actual: roofInfo?.monthlyProductionEstimateData?.February.actual_production ?? 0
                                },
                                {
                                    month: "March",
                                    actual: roofInfo?.monthlyProductionEstimateData?.March.actual_production ?? 0
                                },
                                {
                                    month: "April",
                                    actual: roofInfo?.monthlyProductionEstimateData?.April.actual_production ?? 0
                                },
                                {
                                    month: "May",
                                    actual: roofInfo?.monthlyProductionEstimateData?.May.actual_production ?? 0
                                },
                                {
                                    month: "June",
                                    actual: roofInfo?.monthlyProductionEstimateData?.June.actual_production ?? 0
                                },
                                {
                                    month: "July",
                                    actual: roofInfo?.monthlyProductionEstimateData?.July.actual_production ?? 0
                                },
                                {
                                    month: "August",
                                    actual: roofInfo?.monthlyProductionEstimateData?.August.actual_production ?? 0
                                },
                                {
                                    month: "September",
                                    actual: roofInfo?.monthlyProductionEstimateData?.September.actual_production ?? 0
                                },
                                {
                                    month: "October",
                                    actual: roofInfo?.monthlyProductionEstimateData?.October.actual_production ?? 0
                                },
                                {
                                    month: "November",
                                    actual: roofInfo?.monthlyProductionEstimateData?.November.actual_production ?? 0
                                },
                                {
                                    month: "December",
                                    actual: roofInfo?.monthlyProductionEstimateData?.December.actual_production ?? 0
                                }
                            ]
                        },
                        default_Production_PVWatts: 0, // unused
                        associated_Building: roofAttr?.Associated_Building ?? '',
                        questionable_Roof: !!face.questionableRoof,
                        questionable_Roof_Id: face.questionableRoofId ?? 0,
                        questionable_Roof_Notes: face.questionableRoofNotes ?? '',
                        effic: toInt(roofInfo?.efficiency),
                        prodActual: toInt(roofInfo?.actual_production),
                        prodUtil: toInt(roofInfo?.utility_production),
                        solAcc: toInt(solarAccess),
                        actualPer: '0', // redundant
                        utilPer: '0', // redundant
                        obstructionsDetails: mapJson.obstructions.filter(obst => obst.roofId === face.roofFaceId).map(obst => ({
                            path: '', // redundant
                            base_Point: '', // redundant
                            atts: '',
                            height: obst.height,
                            type: obst.type,
                            is_Toggled: !!obst.delStatus,
                        })),
                        isDeactivated: ifRoofDeactivated(Number(face.roof_tag_id)),
                        // Primary Roof Attrributes
                        rafter_Size: roofAttr?.Rafter_Size ?? "",
                        existing_Rafter_Qty: roofAttr?.Existing_Rafter_Qty ?? 0,
                        unsupported_Rafter_Span: roofAttr?.Unsupported_Rafter_Span ? String(roofAttr?.Unsupported_Rafter_Span) : "",
                        rafter_Spacing: roofAttr?.Rafter_Spacing ?? null,
                        max_Span: maxSpan ?? 0,
                        roof_Type_Id: roofAttr?.Roof_Type_Id ?? 1, // face.surfaceMount ? 2 : 1,
                        structure_Type_Id: roofAttr?.Structure_Type_Id ?? "",
                        roof_Condition_Id: roofAttr?.Roof_Condition_Id ?? null,
                        include_In_Letter: roofAttr?.Include_In_Letter ?? true,
                        psF_Id: roofAttr?.PSF_Id ?? null,
                        notes: roofAttr?.Notes ?? "",
                        dead_Load_Id: roofAttr?.Dead_Load_Id ?? null,
                        roof_Has_Attic_Acces: roofAttr?.Roof_Has_Attic_Acces ?? null,

                        // Additional Roof Attributes
                        existing_Solar_Panels: roofAttr?.Existing_Solar_Panels ?? "",
                        existing_Solar_Area: roofAttr?.Existing_Solar_Panels === 'No' ? 0 
                            : Number(roofAttr?.Existing_Solar_Area) ?? 0,
                        pV_Module_Type: activeModuleName ?? roofAttr?.PV_Module_Type ?? "",
                        rail_Manufacturer: roofAttr?.Rail_Manufacturer ?? "ECOFASTEN",
                        rail_Type: roofAttr?.Rail_Type ?? "STANDARD RAIL",
                        roof_Covering: roofAttr?.Roof_Covering ?? "ASPHALT SHINGLE",
                        attachment_Manufacturer: roofAttr?.Attachment_Manufacturer ?? "ECOFASTEN",
                        attachment_Type: roofAttr?.Attachment_Type ?? "SMART FOOT",
                        number_Of_Stories: roofAttr?.Number_Of_Stories ?? null,
                        existing_Damage: roofAttr?.Existing_Damage ?? "NO",
                        truss_Type: roofAttr?.Truss_Type ?? "",
                        // From design
                        roof_Slope: face.roofSlope ?? null,
                        roof_Area: face.roofArea ?? null,
                        pV_Module_Count: face.moduleArr?.length ?? '',
                    }
                }),
            };
        });

        const isTreePayloadValid = () => {
            for (const tree of mapJson.trees) {
                if (
                    !tree ||
                    typeof tree.height !== "number" || tree.height <= 0 ||
                    typeof tree.crown_radius !== "number" || tree.crown_radius <= 0 ||
                    typeof tree.crown_height !== "number" || tree.crown_height < 0 ||
                    typeof tree.trunk_radius !== "number" || tree.trunk_radius < 0 ||
                    typeof tree.latitude !== "number" || tree.latitude === '' || typeof tree.longitude !== "number" || tree.latitude == null || tree.longitude === '' || tree.type == null || tree.type === ''
                ) {
                    return false;
                }
            }
            return true;
        };

        if(!isTreePayloadValid()) {
            throw new Error("Invalid tree data detected. Please ensure all tree entries have valid height, diameter, crown height, trunk radius, latitude, longitude, and type.");
        }

        const treeDetails = mapJson?.trees.map(tree => ({
            height: tree.height,
            latitude: tree.latitude,
            longitude: tree.longitude,
            diameter: tree.crown_radius,
            crown_Height: tree.crown_height,
            trunk_Radius: tree.trunk_radius,
            type: tree.type,
            is_Toggled: !!tree.delStatus,
            is_Removed: false,
            base_Point: '0', // redundant
        }));

        return {
            id: 0,
            sF_Opportunity_Id: salesForceID,
            ideal_Production: idealProductionValue,
            address: address,
            city: city,
            state: stateCode,
            postalcode: postalCode,
            latitude: latitude,
            longitude: longitude,
            module_Type: activeModule?.text,
            module_Wattage: wattage,
            sF_Directlead_Id: directLead,
            ahJ_Town: ahjTown,
            rsaStatus: rsaStatus ?? null,
            annualUsage: annualUsage ?? null,
            designDetails: [
                {
                    solarAccess: weightedSolarAverage,
                    tsrfEfficiency: Number(efficiency),
                    tsrfEfficiencyOverride: efficiencyOverride ?? null,
                    id: designID,
                    design_Name: 'TestDesign1', // unused
                    design_Type_id: 1, // foreign key in draw.Design_Type
                    path: '', // unused
                    created_IP: '',
                    modified_IP: '',
                    active_Ind: true,
                    is_Deleted: false,
                    is_Archived: false,
                    nsrdB_Update_Status: true,
                    system_Size_kWdc: totalSystemSize.toFixed(2),
                    pathways_Ignored: true,
                    multipliersVendor: selectedLossesVendor,
                    production: {
                        actualAnnual: actualWattage,
                    },
                    buildingDetails: buildingDetails,
                    treeDetails: treeDetails,
                    user_Department: user?.application_role_name,
                    // keep Design_Data at very bottom
                    // to be able to see params above in AppInsights
                    Design_Data: JSON.stringify(mapJson),
                },
            ],
        };
    };
};
